import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import type { CallOff } from '../../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import WarehouseDeliveryModal from './WarehouseDeliveryModal';
import DirectDeliveryModal from './DirectDeliveryModal';
import StepIndicator from './CallOffWizardStepIndicator';
import CallOffWizardSupplierStep from './CallOffWizardSupplierStep';
import CallOffWizardDeliveriesStep from './CallOffWizardDeliveriesStep';
import CallOffWizardDestinationStep from './CallOffWizardDestinationStep';
import { useCallOffWizardState } from './useCallOffWizardState';
import { useCallOffWizardSubmit } from './useCallOffWizardSubmit';
import { useCallOffWizardHaulier } from './useCallOffWizardHaulier';

interface CallOffWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** When set, wizard opens in edit mode with existing call-off data */
  editCallOff?: CallOff | null;
  /** When set, the supplier picker is pre-filled with this supplier and the
   *  wizard advances straight to Step 2 — used by Stock Planning's "New
   *  Call Off" button so the user doesn't pick the supplier twice. */
  initialSupplierId?: number | null;
  /** CR-6293: seed the Open PO Days window from the launching context (e.g. the
   *  Stock Planning grid's Open PO Days), so the wizard inherits it instead of
   *  resetting to the default. */
  initialOpenPoDays?: number | null;
  /** CR-8191: seed the "Committed by" date from Stock Planning's SAL Committed
   *  End Date; empty or absent means today. */
  initialCommittedByDate?: string | null;
}

export default function CallOffWizard({
  open, onClose, onCreated, editCallOff, initialSupplierId, initialOpenPoDays, initialCommittedByDate,
}: CallOffWizardProps) {
  const {
    isEditMode,
    step, setStep,
    loadingSuppliers,
    selectedSupplier, setSelectedSupplier,
    pickupDate, setPickupDate,
    openPoDays, setOpenPoDays,
    committedByDate, setCommittedByDate,
    notes, setNotes,
    deliveries,
    supplierSearch, setSupplierSearch,
    supplierDropdownOpen, setSupplierDropdownOpen,
    warehouses,
    isStaleDelivery, hasStaleDeliveries,
    filteredSuppliers,
    showWarehouseModal, setShowWarehouseModal,
    showDirectModal, setShowDirectModal,
    setEditingDeliveryIndex,
    callOffConfig,
    fillPercent, totalQty, fillPercentExcludingEdited,
    generatedReference,
    resetWizard,
    handleClose,
    handleSupplierChange,
    selectedSupplierStat,
    handleAddWarehouseDelivery,
    handleAddDirectDeliveries,
    handleRemoveDelivery,
    handleEditDelivery,
    handleBackFromStep2,
    editDelivery,
  } = useCallOffWizardState({
    open, onClose, editCallOff, initialSupplierId, initialOpenPoDays, initialCommittedByDate,
  });

  const {
    haulierOptions, loadingHauliers, haulierId, setHaulierId, haulierLocked,
  } = useCallOffWizardHaulier(open, editCallOff);

  const { saving, handleCreate } = useCallOffWizardSubmit({
    selectedSupplier, pickupDate, openPoDays, notes, haulierId, deliveries, fillPercent,
    isEditMode, editCallOff, resetWizard, onCreated, onClose,
  });

  return (
    <>
      <Dialog open={open && !showWarehouseModal && !showDirectModal} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? `Call-Off: ${editCallOff?.reference}` : 'New Call Off'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Edit deliveries, quantities, and pickup details.' : 'Create a new call-off with one or more deliveries.'}
            </DialogDescription>
          </DialogHeader>

          <StepIndicator currentStep={step} />

          {/* Step 1: Select Supplier (hidden in edit mode) */}
          {step === 1 && !isEditMode && (
            <CallOffWizardSupplierStep
              loadingSuppliers={loadingSuppliers}
              selectedSupplier={selectedSupplier}
              setSelectedSupplier={setSelectedSupplier}
              supplierSearch={supplierSearch}
              setSupplierSearch={setSupplierSearch}
              supplierDropdownOpen={supplierDropdownOpen}
              setSupplierDropdownOpen={setSupplierDropdownOpen}
              filteredSuppliers={filteredSuppliers}
              handleSupplierChange={handleSupplierChange}
              generatedReference={generatedReference}
              selectedSupplierStat={selectedSupplierStat}
            />
          )}

          {/* Step 2: Deliveries Hub */}
          {step === 2 && (
            <CallOffWizardDeliveriesStep
              generatedReference={generatedReference}
              supplierName={selectedSupplier?.name}
              pickupDate={pickupDate}
              setPickupDate={setPickupDate}
              notes={notes}
              setNotes={setNotes}
              haulierOptions={haulierOptions}
              loadingHauliers={loadingHauliers}
              haulierId={haulierId}
              setHaulierId={setHaulierId}
              haulierLocked={haulierLocked}
              fillPercent={fillPercent}
              totalQty={totalQty}
              deliveries={deliveries}
              hasStaleDeliveries={hasStaleDeliveries}
              isStaleDelivery={isStaleDelivery}
              handleEditDelivery={handleEditDelivery}
              handleRemoveDelivery={handleRemoveDelivery}
              onAddDelivery={() => setStep(3)}
            />
          )}

          {/* Step 3: Choose Delivery Type */}
          {step === 3 && (
            <CallOffWizardDestinationStep
              callOffConfig={callOffConfig}
              onChooseWarehouse={() => {
                setEditingDeliveryIndex(null);
                setShowWarehouseModal(true);
              }}
              onChooseDirect={() => {
                setEditingDeliveryIndex(null);
                setShowDirectModal(true);
              }}
            />
          )}

          <DialogFooter>
            {step === 1 && (
              <>
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedSupplier}
                  className="gap-2"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                {!isEditMode && (
                  <Button variant="outline" onClick={handleBackFromStep2} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                )}
                {isEditMode && (
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleCreate}
                  disabled={saving || !pickupDate || deliveries.length === 0 || hasStaleDeliveries}
                  title={hasStaleDeliveries ? 'Fix flagged deliveries before saving' : undefined}
                  className="gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isEditMode ? 'Save Changes' : 'Create Call-Off'}
                </Button>
              </>
            )}
            {step === 3 && (
              <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse delivery modal */}
      {selectedSupplier && (
        <WarehouseDeliveryModal
          open={showWarehouseModal}
          onClose={() => {
            setShowWarehouseModal(false);
            setEditingDeliveryIndex(null);
          }}
          onAdd={handleAddWarehouseDelivery}
          supplierId={selectedSupplier.id}
          existingFillPercent={fillPercentExcludingEdited}
          editDelivery={editDelivery?.type === 'warehouse' ? editDelivery : undefined}
          warehouses={warehouses}
          openPoDays={openPoDays}
          setOpenPoDays={setOpenPoDays}
          pickupDate={pickupDate}
          setPickupDate={setPickupDate}
          committedByDate={committedByDate}
          setCommittedByDate={setCommittedByDate}
        />
      )}

      {/* Direct delivery modal */}
      {selectedSupplier && (
        <DirectDeliveryModal
          open={showDirectModal}
          onClose={() => {
            setShowDirectModal(false);
            setEditingDeliveryIndex(null);
          }}
          onAdd={handleAddDirectDeliveries}
          supplierId={selectedSupplier.id}
          supplierName={selectedSupplier.name}
          existingFillPercent={fillPercentExcludingEdited}
          editDelivery={editDelivery?.type === 'direct' ? editDelivery : undefined}
        />
      )}
    </>
  );
}
